(() => {

  const PIN_PREVIEW_IMAGE_LOAD_TIMEOUT_MS = 8000;

  class PinPreviewCanvas {
    constructor(canvasEl) {
      this.canvas = canvasEl;
      this.ctx = canvasEl.getContext('2d');
      this.pinImage = null;
      this.pinUrl = null;
      this.text = '';
      this.pinFocal = { x: 0.5, y: 0.5 };
      this.fontSize = 32;
      this.renderPending = false;
      this._pinGen = 0;
    }

    _loadImage(url) {
      return new Promise((resolve) => {
        if (!url) return resolve(null);
        const img = new Image();
        img.crossOrigin = 'anonymous';
        const timeout = setTimeout(() => resolve(null), PIN_PREVIEW_IMAGE_LOAD_TIMEOUT_MS);
        img.onload = () => { clearTimeout(timeout); resolve(img); };
        img.onerror = () => { clearTimeout(timeout); resolve(null); };
        img.src = url;
      });
    }

    async setPinUrl(url) {
      if (url === this.pinUrl) return;
      this.pinUrl = url;
      const gen = ++this._pinGen;
      const img = await this._loadImage(url);
      if (gen !== this._pinGen) return;
      this.pinImage = img;
      this.render();
    }

    setText(text) {
      this.text = text || '';
      this.render();
    }

    setPinFocal(x, y) {
      this.pinFocal = { x, y };
      this.render();
    }

    setFontSize(size) {
      const parsed = parseFloat(size);
      if (Number.isFinite(parsed) && parsed > 0) {
        this.fontSize = parsed;
        this.render();
      }
    }

    render() {
      if (this.renderPending) return;
      this.renderPending = true;
      requestAnimationFrame(() => {
        this.renderPending = false;
        this._draw();
      });
    }

    _draw() {
      const w = this.canvas.width;
      const h = this.canvas.height;

      this.ctx.clearRect(0, 0, w, h);
      this.ctx.fillStyle = '#f3f4f6';
      this.ctx.fillRect(0, 0, w, h);

      if (!this.pinImage) return;
      this._drawImage(this.pinImage, 0, 0, w, h);

      if (this.text) {
        const textCx = w * this.pinFocal.x;
        const textCy = h * this.pinFocal.y;
        this._drawText(textCx, textCy, this.fontSize);
      }
    }

    _drawImage(img, x, y, w, h) {
      const imgAspect = img.width / img.height;
      const boxAspect = w / h;
      const lockHeight = imgAspect <= boxAspect;
      let dw, dh, dx, dy;
      if (lockHeight) {
        dh = h;
        dw = h * imgAspect;
        dx = x + (w - dw) / 2;
        dy = y;
      } else {
        dw = w;
        dh = w / imgAspect;
        dx = x;
        dy = y + (h - dh) / 2;
      }
      this.ctx.drawImage(img, dx, dy, dw, dh);
    }

    _drawText(cx, cy, fontSizeOverride) {
      this.ctx.save();
      this.ctx.translate(cx, cy);
      this.ctx.rotate(-Math.PI / 2);
      this.ctx.font = `600 ${fontSizeOverride ?? this.fontSize}px Montserrat, sans-serif`;
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(this.text, 0, 0);
      this.ctx.restore();
    }
  }

  class ProductTabs extends HTMLElement {
    constructor() {
      super();

      this.productId = null;
      this.activeTab = 'colors';
      this.tabButtons = [];
      this.tabContents = [];
      this.variantSelectorWrapper = null;
      this.designEmptyState = null;
      this.designPreviewsContainer = null;
      this.designPreviewsGrid = null;
      this.customiseButton = null;
      this.selectedDesignVariantId = null;
      this.pinCanvases = new Map();

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleCustomiseClick = this.handleCustomiseClick.bind(this);
      this.handleDesignPreviewClick = this.handleDesignPreviewClick.bind(this);
      this.handleCustomizationApplied = this.handleCustomizationApplied.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    connectedCallback() {
      this.init();
      this.initMokoPinSelector();
      this.initMokoPinCanvases();
      this.updateCustomizeButtonVisibility();
      this.updateYourDesignTabVisibility();
      this.updateMokoPinContainerVisibility();
      this.syncMokoPinPreviewLayers();

      if (this.dataset.isSelfMokoPin === 'true') {
        setTimeout(() => {
          const variantSwitcher = document.querySelector('product-variant-switcher');
          if (variantSwitcher?.currentVariantId) {
            this.syncTabPinSelection(variantSwitcher.currentVariantId);
          }
        }, 150);
      }
    }

    disconnectedCallback() {
      this.tabButtons.forEach(button => {
        button.removeEventListener('click', this.handleTabClick);
      });

      if (this.customiseButton) {
        this.customiseButton.removeEventListener('click', this.handleCustomiseClick);
      }

      if (this.mokoPinEditButton) {
        this.mokoPinEditButton.removeEventListener('click', this.handleCustomiseClick);
      }

      document.removeEventListener('customization:applied', this.handleCustomizationApplied);
      document.removeEventListener('variant:changed', this.handleVariantChange);
    }

    init() {
      this.productId = this.dataset.productId;
      if (!this.productId) return;

      this.tabButtons = Array.from(this.querySelectorAll('[data-tab]'));

      this.tabContents = Array.from(this.querySelectorAll('[data-tab-content]'));

      this.variantSelectorWrapper = this.querySelector('.variant-selector-wrapper');

      this.designEmptyState = this.querySelector('[data-design-empty-state]');
      this.designPreviewsContainer = this.querySelector('[data-design-previews]');
      this.designPreviewsGrid = this.querySelector('[data-design-previews-grid]');
      this.customiseButton = this.querySelector('[data-customise-button]');
      this.mokoPinEditButton = this.querySelector('[data-moko-pin-edit-button]');

      this.tabButtons.forEach(button => {
        button.addEventListener('click', this.handleTabClick);
      });

      if (this.customiseButton) {
        this.customiseButton.addEventListener('click', this.handleCustomiseClick);
      }

      if (this.mokoPinEditButton) {
        this.mokoPinEditButton.addEventListener('click', this.handleCustomiseClick);
      }

      document.addEventListener('customization:applied', this.handleCustomizationApplied);
      document.addEventListener('variant:changed', this.handleVariantChange);

      const initialTab = this.dataset.isSelfMokoPin === 'true' ? 'design' : 'colors';
      this.activeTab = null;
      this.switchTab(initialTab);
      this.loadDesignPreviews();

      this.initializeColorSwatches();
    }

    initializeColorSwatches() {
      setTimeout(() => {
        const variantSwitcher = document.querySelector('product-variant-switcher');
        if (!variantSwitcher) return;

        const variantSelector = this.querySelector('variant-selector');
        if (variantSelector) {
          const colorSwatches = variantSelector.querySelectorAll('[data-variant-swatch]');

          if (variantSwitcher.swatches && colorSwatches.length > 0) {
            colorSwatches.forEach(swatch => {
              const isIncluded = Array.from(variantSwitcher.swatches).some(s => s === swatch);
              if (!isIncluded) {
                variantSwitcher.swatches.push(swatch);
                swatch.addEventListener('click', (e) => {
                  variantSwitcher.handleSwatchClick.call(variantSwitcher, e);
                });
              }
            });
          } else if (colorSwatches.length > 0 && variantSwitcher.handleSwatchClick) {
            colorSwatches.forEach(swatch => {
              if (!swatch.dataset.listenerAttached) {
                swatch.addEventListener('click', (e) => {
                  variantSwitcher.handleSwatchClick.call(variantSwitcher, e);
                });
                swatch.dataset.listenerAttached = 'true';
              }
            });
          }
        }
      }, 200);
    }

    initMokoPinSelector() {
      const selector = this.querySelector('[data-moko-pin-selector]');
      if (!selector) return;

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';

      selector.addEventListener('click', (event) => {
        const option = event.target.closest('[data-moko-pin-variant-id]');
        if (!option || option.disabled) return;

        const allOptions = selector.querySelectorAll('[data-moko-pin-variant-id]');
        allOptions.forEach((opt) => {
          opt.removeAttribute('data-selected');
          opt.classList.remove('border-black', 'ring-1', 'ring-black');
          opt.classList.add('border-gray-200', 'hover:border-gray-400');
        });

        option.setAttribute('data-selected', 'true');
        option.classList.remove('border-gray-200', 'hover:border-gray-400');
        option.classList.add('border-black', 'ring-1', 'ring-black');

        if (isSelfMokoPin) {
          const variantId = parseInt(option.dataset.mokoPinVariantId, 10);
          if (!Number.isFinite(variantId)) return;

          const variantSwitcher = document.querySelector('product-variant-switcher');
          if (!variantSwitcher) return;

          const variant = variantSwitcher.productData?.variants?.find(v => v.id === variantId);
          if (!variant || variantSwitcher.currentVariantId === variantId) return;

          variantSwitcher.currentVariantId = variantId;
          variantSwitcher.updateSwatchesState?.(variantId);
          variantSwitcher.updateMediaVisibility?.(variantId);
          variantSwitcher.updatePrice?.(variant);
          variantSwitcher.updateForm?.(variant);
          variantSwitcher.updateVariantTitle?.(variant);
          variantSwitcher.updateURL?.(variantId);

          document.dispatchEvent(new CustomEvent('variant:changed', {
            detail: { variantId, variant },
            bubbles: true,
          }));
        }
      });
    }

    syncTabPinSelection(pinVariantId) {
      const selector = this.querySelector('[data-moko-pin-selector]');
      if (!selector || !pinVariantId) return;

      const allCards = selector.querySelectorAll('[data-moko-pin-variant-id]');
      allCards.forEach((card) => {
        if (card.dataset.mokoPinVariantId === String(pinVariantId)) {
          card.setAttribute('data-selected', 'true');
          card.classList.remove('border-gray-200', 'hover:border-gray-400');
          card.classList.add('border-black', 'ring-1', 'ring-black');
        } else {
          card.removeAttribute('data-selected');
          card.classList.remove('border-black', 'ring-1', 'ring-black');
          card.classList.add('border-gray-200', 'hover:border-gray-400');
        }
      });
    }

    // Moko Pin selector is engraving-text-only by design — the pin overlay
    // carries the text, and the cart payload requires Engraving Text.
    // Hide the selector until text is set.
    updateMokoPinContainerVisibility() {
      const container = this.querySelector('[data-moko-pin-container]');
      if (!container) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      const variantId = this.selectedDesignVariantId || variantSwitcher?.currentVariantId;
      if (!variantId || !this.productId) {
        container.classList.add('hidden');
        return;
      }

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const storageKey = isSelfMokoPin
        ? `customization_selfpin_${this.productId}`
        : `customization_${this.productId}_${variantId}`;
      const savedData = localStorage.getItem(storageKey);
      let hasEngravingText = false;

      if (savedData) {
        try {
          hasEngravingText = !!JSON.parse(savedData).text?.content;
        } catch (e) {
          console.error('[ProductTabs] Failed to parse saved data for pin visibility:', e);
        }
      }

      container.classList.toggle('hidden', !hasEngravingText);
    }

    initMokoPinCanvases() {
      const container = this.querySelector('[data-moko-pin-container]');
      if (!container) return;

      const cards = container.querySelectorAll('[data-moko-pin-card]');
      cards.forEach((card) => {
        const canvasEl = card.querySelector('[data-moko-pin-canvas]');
        if (!canvasEl) return;

        const preview = new PinPreviewCanvas(canvasEl);
        this.pinCanvases.set(card, preview);

        const pinUrl = card.dataset.pinImageUrl || '';
        const pinFx = parseFloat(card.dataset.pinFocalX);
        const pinFy = parseFloat(card.dataset.pinFocalY);
        preview.setPinFocal(
          Number.isFinite(pinFx) ? pinFx : 0.5,
          Number.isFinite(pinFy) ? pinFy : 0.5
        );

        preview.setFontSize(card.dataset.addonFontSize);

        if (pinUrl) preview.setPinUrl(pinUrl);
      });
    }

    syncMokoPinPreviewLayers() {
      if (!this.pinCanvases || this.pinCanvases.size === 0) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return;

      const activeVariantId = this.selectedDesignVariantId || variantSwitcher.currentVariantId;
      if (!activeVariantId) return;

      const activeVariant = variantSwitcher.productData?.variants?.find(v => v.id === activeVariantId);
      if (!activeVariant) return;

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const storageKey = isSelfMokoPin
        ? `customization_selfpin_${this.productId}`
        : `customization_${this.productId}_${activeVariantId}`;
      const savedData = localStorage.getItem(storageKey);
      let textContent = '';

      if (savedData) {
        try {
          textContent = JSON.parse(savedData)?.text?.content || '';
        } catch (e) {
          console.error('[ProductTabs] Failed to parse customization data for pin layers:', e);
        }
      }

      this.pinCanvases.forEach((preview) => {
        preview.setText(textContent);
      });
    }

    handleTabClick(event) {
      event.preventDefault();
      const tabName = event.currentTarget.dataset.tab;
      this.switchTab(tabName);
    }

    switchTab(tabName) {
      if (tabName === 'compare') {
        const compareSection = document.querySelector('.product-compare-section');
        if (compareSection) {
          const headerOffset = 100;
          const elementPosition = compareSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
        return;
      }

      if (this.activeTab === tabName) return;

      this.activeTab = tabName;

      this.tabButtons.forEach(button => {
        const buttonTab = button.dataset.tab;
        if (buttonTab === tabName) {
          button.classList.remove('border-transparent', 'text-[#989595]');
          button.classList.add('border-black', 'text-black');
        } else {
          button.classList.remove('border-black', 'text-black');
          button.classList.add('border-transparent', 'text-[#989595]');
        }
      });

      this.tabContents.forEach(content => {
        const contentTab = content.dataset.tabContent;
        if (contentTab === tabName) {
          content.classList.remove('hidden');
        } else {
          content.classList.add('hidden');
        }
      });

      if (this.variantSelectorWrapper) {
        if (tabName === 'colors') {
          this.variantSelectorWrapper.classList.remove('hidden');
        } else {
          this.variantSelectorWrapper.classList.add('hidden');
        }
      }

      if (tabName === 'design') {
        this.loadDesignPreviews();

        const variantSwitcher = document.querySelector('product-variant-switcher');

        if (variantSwitcher?.currentVariantId) {
          const currentVariantId = variantSwitcher.currentVariantId;
          const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
          const storageKey = isSelfMokoPin
            ? `customization_selfpin_${this.productId}`
            : `customization_${this.productId}_${currentVariantId}`;
          const savedData = localStorage.getItem(storageKey);

          if (savedData) {
            try {
              const customizationData = JSON.parse(savedData);
              const hasContent = !!(
                (customizationData.text && customizationData.text.content) ||
                customizationData.pattern ||
                customizationData.color
              );

              if (hasContent) {
                this.handleDesignPreviewClick(currentVariantId);
              }
            } catch (e) {
              console.error('[ProductTabs] Failed to parse saved data:', e);
            }
          }
        }

      } else if (tabName === 'colors') {
        this.selectedDesignVariantId = null;
      }

      this.updateCustomizeButtonVisibility();
      this.updateYourDesignTabVisibility();
      this.dispatchTabChanged();
    }

    dispatchTabChanged() {
      const event = new CustomEvent('tab:changed', {
        detail: {
          activeTab: this.activeTab,
          productId: this.productId
        },
        bubbles: true
      });
      this.dispatchEvent(event);
    }

    loadDesignPreviews() {
      if (!this.productId || !this.designPreviewsGrid) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return;

      const productData = variantSwitcher.productData;
      if (!productData || !productData.variants) return;

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';

      if (isSelfMokoPin) {
        const sharedKey = `customization_selfpin_${this.productId}`;
        const savedData = localStorage.getItem(sharedKey);
        const currentVariantId = variantSwitcher.currentVariantId;

        let hasContent = false;
        if (savedData && currentVariantId) {
          try {
            const customizationData = JSON.parse(savedData);
            hasContent = !!(customizationData.text?.content || customizationData.pattern || customizationData.color);
          } catch (e) {
            console.error('[ProductTabs] Failed to parse saved selfMokoPin customization:', e);
          }
        }

        if (hasContent) {
          if (this.designEmptyState) this.designEmptyState.classList.add('hidden');
          if (!this.selectedDesignVariantId) {
            this.handleDesignPreviewClick(currentVariantId);
          }
        } else {
          this.selectedDesignVariantId = null;
          if (this.designEmptyState) this.designEmptyState.classList.remove('hidden');
          if (this.designPreviewsContainer) this.designPreviewsContainer.classList.add('hidden');
        }

        this.updateCustomizeButtonVisibility();
        this.updateYourDesignTabVisibility();
        return;
      }

      const savedDesigns = [];

      productData.variants.forEach(variant => {
        const storageKey = `customization_${this.productId}_${variant.id}`;
        const savedData = localStorage.getItem(storageKey);

        if (savedData) {
          try {
            const customizationData = JSON.parse(savedData);
            const hasContent = !!(
              (customizationData.text?.content) ||
              customizationData.pattern ||
              customizationData.color
            );

            if (hasContent) {
              savedDesigns.push({
                variantId: variant.id,
                variantTitle: variant.title,
                customizationData,
                previewImage: customizationData.previewImage || null
              });
            }
          } catch (e) {
            console.error('[ProductTabs] Failed to parse saved customization:', e);
          }
        }
      });

      if (savedDesigns.length > 0) {
        const hasMokoPin = this.dataset.hasMokoPin === 'true';

        if (this.designEmptyState) {
          this.designEmptyState.classList.add('hidden');
        }
        // For MOKO_PIN products, keep design previews hidden — pin cards
        // serve as the sole design preview. Showing then re-hiding would
        // cause a visible flicker.
        if (!hasMokoPin && this.designPreviewsContainer) {
          this.designPreviewsContainer.classList.remove('hidden');
        }

        this.renderDesignPreviews(savedDesigns, hasMokoPin);

        const selectedDesignExists = savedDesigns.some(
          design => design.variantId === this.selectedDesignVariantId
        );

        if (!this.selectedDesignVariantId || !selectedDesignExists) {
          const firstDesign = savedDesigns[0];
          this.handleDesignPreviewClick(firstDesign.variantId);
        }

        this.updateCustomizeButtonVisibility();
        this.updateYourDesignTabVisibility();
        this.updateMokoPinContainerVisibility();
        this.syncMokoPinPreviewLayers();
        return;
      }
      this.selectedDesignVariantId = null;

      if (this.designEmptyState) {
        this.designEmptyState.classList.remove('hidden');
      }
      if (this.designPreviewsContainer) {
        this.designPreviewsContainer.classList.add('hidden');
      }

      this.updateCustomizeButtonVisibility();
      this.updateYourDesignTabVisibility();
      this.updateMokoPinContainerVisibility();
      this.syncMokoPinPreviewLayers();
    }

    renderDesignPreviews(designs, hasMokoPin) {
      if (!this.designPreviewsGrid) return;

      const template = this.querySelector('[data-design-preview-template]');
      if (!template) {
        console.error('[ProductTabs] Design preview template not found');
        return;
      }

      this.designPreviewsGrid.innerHTML = '';

      designs.forEach((design, index) => {
        const clone = template.content.cloneNode(true);
        const item = clone.querySelector('.design-preview-item');

        item.dataset.designId = design.id || Date.now().toString();
        item.dataset.variantId = design.variantId;
        item.dataset.designIndex = index;

        const isSelected = this.selectedDesignVariantId === design.variantId;
        if (isSelected) {
          item.classList.remove('border-transparent', 'hover:border-black');
          item.classList.add('border-black');

          const editBtn = item.querySelector('.design-edit-button');
          if (editBtn) {
            editBtn.classList.remove('opacity-0', 'group-hover:opacity-100');
            editBtn.classList.add('opacity-100');
          }
        }

        if (design.previewImage && design.previewImage.trim() !== '') {
          const img = item.querySelector('.design-preview-image');
          const placeholder = item.querySelector('.design-preview-placeholder');

          img.src = design.previewImage;
          img.alt = `Design ${index + 1}`;
          img.classList.remove('hidden');
          placeholder.classList.add('hidden');

          img.addEventListener('error', () => {
            img.classList.add('hidden');
            placeholder.classList.remove('hidden');
            placeholder.querySelector('.design-preview-text').textContent = design.variantTitle || 'Design';
          });
        } else {
          const img = item.querySelector('.design-preview-image');
          const placeholder = item.querySelector('.design-preview-placeholder');

          img.classList.add('hidden');
          placeholder.classList.remove('hidden');
          placeholder.querySelector('.design-preview-text').textContent = design.variantTitle || 'Design';
        }

        item.addEventListener('click', () => {
          this.handleDesignPreviewClick(design.variantId);
        });

        const editBtn = item.querySelector('.design-edit-button');
        if (editBtn) {
          editBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.handleDesignPreviewClick(design.variantId);
            this.openCustomizeModalForVariant(design.variantId);
          });
        }

        this.designPreviewsGrid.appendChild(item);
      });
    }

    handleDesignPreviewClick(variantId) {
      this.selectedDesignVariantId = variantId;

      const previewItems = this.designPreviewsGrid.querySelectorAll('.design-preview-item');
      previewItems.forEach(item => {
        const editButton = item.querySelector('button[aria-label="Edit design"]');

        if (item.dataset.variantId === String(variantId)) {
          item.classList.remove('border-transparent', 'hover:border-black');
          item.classList.add('border-black');

          if (editButton) {
            editButton.classList.remove('opacity-0', 'group-hover:opacity-100');
            editButton.classList.add('opacity-100');
          }
          return;
        }
        item.classList.remove('border-black');
        item.classList.add('border-transparent', 'hover:border-black');

        if (editButton) {
          editButton.classList.remove('opacity-100');
          editButton.classList.add('opacity-0', 'group-hover:opacity-100');
        }
      });

      const storageKey = `customization_${this.productId}_${variantId}`;
      const savedData = localStorage.getItem(storageKey);

      if (savedData) {
        try {
          const customizationData = JSON.parse(savedData);
          const productForms = document.querySelectorAll('product-form');
          productForms.forEach(productForm => {
            productForm.dataset.customizationData = JSON.stringify(customizationData);
          });
        } catch (e) {
          console.error('[ProductTabs] Failed to parse customization data:', e);
        }
      }

      this.dispatchTabChanged();
      this.syncMokoPinPreviewLayers();
    }

    handleCustomiseClick(event) {
      event.preventDefault();

      if (!this.productId) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return;

      const currentVariantId = variantSwitcher.currentVariantId;
      const productData = variantSwitcher.productData;

      if (!productData || !currentVariantId) return;

      const variant = productData.variants?.find(v => v.id === currentVariantId);
      if (!variant) return;

      const modal = document.getElementById(`product-customize-modal-${this.productId}`);
      if (!modal) {
        console.error('[ProductTabs] Modal not found:', `product-customize-modal-${this.productId}`);
        return;
      }

      this.selectedDesignVariantId = currentVariantId;

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const storageKey = isSelfMokoPin
        ? `customization_selfpin_${this.productId}`
        : `customization_${this.productId}_${currentVariantId}`;
      const savedData = localStorage.getItem(storageKey);

      const hasMokoPin = this.dataset.hasMokoPin === 'true';

      if (isSelfMokoPin) {
        // For selfMokoPin, sync the modal pin selector to the current variant
        // so the canvas loads the correct colour engraving image.
        if (typeof modal.setSelectedMokoPinVariant === 'function') {
          modal.setSelectedMokoPinVariant(String(currentVariantId));
        }
      } else if (hasMokoPin) {
        const tabSelectedPin = this.querySelector('[data-moko-pin-container] [data-selected="true"]');
        if (tabSelectedPin?.dataset.mokoPinVariantId && modal.setSelectedMokoPinVariant) {
          modal.setSelectedMokoPinVariant(tabSelectedPin.dataset.mokoPinVariantId);
        }
      } else {
        if (!variant.engraving_image_url) {
          console.warn('[ProductTabs] Variant does not have engraving_image_url');
          return;
        }

        if (modal.updateEngravingData) {
          modal.updateEngravingData(
            variant.engraving_image_url,
            variant.engraving_position,
            variant.engraving_font_size
          );
        }
      }

      if (savedData) {
        try {
          const customizationData = JSON.parse(savedData);
          this.loadSavedCustomization(modal, customizationData);
        } catch (e) {
          console.error('[ProductTabs] Failed to parse saved customization:', e);
        }
      }

      modal.open();
    }

    loadSavedCustomization(modal, customizationData) {
      if (!modal || !customizationData) return;

      const loadCustomization = () => {
        if (!modal.canvas) {
          setTimeout(loadCustomization, 100);
          return;
        }

        if (modal.state) {
          if (customizationData.text) {
            Object.assign(modal.state.text, customizationData.text);
            if (customizationData.text.content) {
              modal.state.text.enabled = true;
            }
          }
          if (customizationData.pattern) {
            modal.state.pattern = customizationData.pattern;
          }
          if (customizationData.color) {
            modal.state.color = customizationData.color;
          }
        }

        if (customizationData.text?.content && modal.textInput) {
          modal.textInput.value = customizationData.text.content;
          if (modal.updateTextLength) {
            modal.updateTextLength();
          }
          if (modal.canvas && modal.state.text) {
            modal.canvas.setText(modal.getCanvasTextState());
          }
        }

        if (customizationData.pattern && modal.canvas) {
          modal.canvas.setPattern(customizationData.pattern).then(() => {
            modal.canvas.render();
          });
        }

        if (customizationData.color && modal.canvas) {
          modal.canvas.setColor(customizationData.color);
        }

        if (modal.snapshotInitialState) {
          modal.snapshotInitialState();
        }
      };

      setTimeout(loadCustomization, 300);
    }

    handleCustomizationApplied(event) {
      const customizationData = event.detail;
      if (!customizationData || !this.productId) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return;

      // Save against the live currentVariantId so a mid-edit variant switch
      // doesn't write to a stale selectedDesignVariantId.
      const currentVariantId = variantSwitcher.currentVariantId;
      if (!currentVariantId) return;

      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const storageKey = isSelfMokoPin
        ? `customization_selfpin_${this.productId}`
        : `customization_${this.productId}_${currentVariantId}`;

      const hasContent = !!(
        (customizationData.text && customizationData.text.content) ||
        customizationData.pattern ||
        customizationData.color
      );

      if (!hasContent) {
        localStorage.removeItem(storageKey);

        document.querySelectorAll('product-form').forEach((productForm) => {
          delete productForm.dataset.customizationData;
        });

        setTimeout(() => {
          this.loadDesignPreviews();
          this.updateCustomizeButtonVisibility();
          this.updateYourDesignTabVisibility();
          this.updateMokoPinContainerVisibility();
          this.syncMokoPinPreviewLayers();
        }, 150);
        return;
      }

      let previewImage = customizationData.previewImage || null;

      if (!previewImage) {
        const modal = document.getElementById(`product-customize-modal-${this.productId}`);
        if (modal && modal.canvas) {
          try {
            const canvasElement = modal.canvas.canvas || modal.canvas;
            if (canvasElement && typeof canvasElement.toDataURL === 'function') {
              previewImage = canvasElement.toDataURL('image/png');
            }
          } catch (e) {
            console.error('[ProductTabs] Failed to capture preview image as fallback:', e);
          }
        }
      }

      let dataToStore = { ...customizationData };

      try {
        const existingData = localStorage.getItem(storageKey);
        if (existingData) {
          const existing = JSON.parse(existingData);
          dataToStore = { ...existing, ...customizationData };
        }
      } catch (e) {
        console.error('[ProductTabs] Failed to parse existing data:', e);
      }

      if (previewImage) {
        dataToStore.previewImage = previewImage;
      }

      localStorage.setItem(storageKey, JSON.stringify(dataToStore));

      if (customizationData.selectedMokoPinVariantId) {
        this.syncTabPinSelection(customizationData.selectedMokoPinVariantId);
      }

      setTimeout(() => {
        this.loadDesignPreviews();
        this.handleDesignPreviewClick(currentVariantId);
        this.updateCustomizeButtonVisibility();
        this.updateYourDesignTabVisibility();
        this.updateMokoPinContainerVisibility();
        this.syncMokoPinPreviewLayers();
        this.switchTab('design');
      }, 150);
    }

    handleVariantChange(event) {
      // Re-resolve selectedDesignVariantId so downstream sync calls don't
      // operate on a stale value from a previously-edited variant.
      const newVariantId = event?.detail?.variantId;
      if (newVariantId && this.productId) {
        const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
        const storageKey = isSelfMokoPin
          ? `customization_selfpin_${this.productId}`
          : `customization_${this.productId}_${newVariantId}`;
        const savedData = localStorage.getItem(storageKey);
        let hasSavedDesign = false;
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            hasSavedDesign = !!(parsed.text?.content || parsed.pattern || parsed.color);
          } catch (e) {
            console.error('[ProductTabs] Failed to parse saved data on variant change:', e);
          }
        }
        this.selectedDesignVariantId = hasSavedDesign ? newVariantId : null;

        if (isSelfMokoPin) {
          this.syncTabPinSelection(newVariantId);
        }
      }

      this.updateCustomizeButtonVisibility();
      this.updateYourDesignTabVisibility();
      this.updateMokoPinContainerVisibility();
      this.syncMokoPinPreviewLayers();
      this.updateSelectedOptionLabels(event);
    }

    updateSelectedOptionLabels(event) {
      const variantId = event?.detail?.variantId;
      if (!variantId) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      const variant = variantSwitcher?.productData?.variants?.find(v => v.id === variantId);
      if (!variant) return;

      this.querySelectorAll('[data-selected-option-label][data-option-position]').forEach(label => {
        const position = parseInt(label.dataset.optionPosition, 10);
        const value = variant[`option${position}`];
        if (value) label.textContent = value;
      });
    }

    openCustomizeModalForVariant(variantId) {
      if (!this.productId || !variantId) return;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return;

      const productData = variantSwitcher.productData;
      if (!productData) return;

      const variant = productData.variants?.find(v => v.id === variantId);
      if (!variant) return;

      if (variantSwitcher.currentVariantId !== variantId) {
        variantSwitcher.currentVariantId = variantId;
        variantSwitcher.updateSwatchesState(variantId);
        variantSwitcher.updateMediaVisibility(variantId);
        variantSwitcher.updatePrice(variant);
        variantSwitcher.updateForm(variant);
        variantSwitcher.updateVariantTitle(variant);
        variantSwitcher.updateURL(variantId);
      }

      const modal = document.getElementById(`product-customize-modal-${this.productId}`);
      if (!modal) {
        console.error('[ProductTabs] Modal not found:', `product-customize-modal-${this.productId}`);
        return;
      }

      this.selectedDesignVariantId = variantId;

      const storageKey = `customization_${this.productId}_${variantId}`;
      const savedData = localStorage.getItem(storageKey);

      const hasMokoPin = this.dataset.hasMokoPin === 'true';

      if (hasMokoPin) {
        const tabSelectedPin = this.querySelector('[data-moko-pin-container] [data-selected="true"]');
        if (tabSelectedPin?.dataset.mokoPinVariantId && modal.setSelectedMokoPinVariant) {
          modal.setSelectedMokoPinVariant(tabSelectedPin.dataset.mokoPinVariantId);
        }
      } else {
        if (!variant.engraving_image_url) {
          console.warn('[ProductTabs] Variant does not have engraving_image_url');
          return;
        }

        if (modal.updateEngravingData) {
          modal.updateEngravingData(
            variant.engraving_image_url,
            variant.engraving_position,
            variant.engraving_font_size
          );
        }
      }

      if (savedData) {
        try {
          const customizationData = JSON.parse(savedData);
          this.loadSavedCustomization(modal, customizationData);
        } catch (e) {
          console.error('[ProductTabs] Failed to parse saved customization:', e);
        }
      }

      modal.open();
    }

    getActiveTab() {
      return this.activeTab;
    }

    getSelectedDesignVariantId() {
      return this.selectedDesignVariantId;
    }

    updateCustomizeButtonVisibility() {
      const customizeButtons = document.querySelectorAll('[data-customize-button]');
      if (!customizeButtons || customizeButtons.length === 0) return;

      const hasMokoPin = this.dataset.hasMokoPin === 'true';
      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const isMokoPinTagged = hasMokoPin && !isSelfMokoPin;
      const currentVariantHasEngraving = this.currentVariantHasEngravingImage();
      const isCustomisable = hasMokoPin || currentVariantHasEngraving;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      const variantId = variantSwitcher?.currentVariantId;
      const hasSavedForCurrent = !!(variantId && this.hasSavedCustomization(variantId));

      customizeButtons.forEach(customizeButton => {
        const labelEl = customizeButton.querySelector('[data-customize-button-label]');

        // MOKO_PIN-tagged:
        //   • Personalise tab → always hidden (in-tab UI owns the flow)
        //   • any other tab   → visible as "Personalise"
        if (isMokoPinTagged) {
          if (this.activeTab === 'design') {
            customizeButton.classList.add('hidden');
          } else {
            customizeButton.classList.remove('hidden');
            if (labelEl) {
              labelEl.textContent = customizeButton.dataset.defaultLabel || 'Personalise';
            }
          }
          return;
        }

        // Self-pin: standalone is the only edit affordance — show it (as "Edit Personalise")
        // once the current variant has a save, otherwise rely on the in-tab empty-state CTA.
        if (isSelfMokoPin && customizeButton.dataset.selfMokoPin === 'true') {
          if (hasSavedForCurrent) {
            customizeButton.classList.remove('hidden');
            if (labelEl) {
              labelEl.textContent = customizeButton.dataset.editLabel || 'Edit Personalise';
            }
            if (this.designEmptyState) {
              this.designEmptyState.classList.add('hidden');
            }
          } else {
            customizeButton.classList.add('hidden');
            if (this.designEmptyState) {
              this.designEmptyState.classList.remove('hidden');
            }
          }
          return;
        }

        // Engraving product (non-pin): hidden when not customisable; on the design
        // tab, hidden until at least one design exists; otherwise visible.
        if (!isCustomisable) {
          customizeButton.classList.add('hidden');
          return;
        }

        if (this.activeTab === 'design' && this.getDesignCount() === 0) {
          customizeButton.classList.add('hidden');
        } else {
          customizeButton.classList.remove('hidden');
        }
      });
    }

    hasSavedCustomization(variantId) {
      if (!this.productId || !variantId) return false;
      const isSelfMokoPin = this.dataset.isSelfMokoPin === 'true';
      const storageKey = isSelfMokoPin
        ? `customization_selfpin_${this.productId}`
        : `customization_${this.productId}_${variantId}`;
      const savedData = localStorage.getItem(storageKey);
      if (!savedData) return false;
      try {
        const data = JSON.parse(savedData);
        return !!(data.text?.content || data.pattern || data.color);
      } catch (e) {
        return false;
      }
    }

    currentVariantHasEngravingImage() {
      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher || !variantSwitcher.currentVariantId) return false;

      const productData = variantSwitcher.productData;
      if (!productData || !productData.variants) return false;

      const currentVariant = productData.variants.find(
        v => v.id === variantSwitcher.currentVariantId
      );

      if (!currentVariant) return false;

      return !!(currentVariant.engraving_image_url);
    }

    updateYourDesignTabVisibility() {
      const designTab = this.tabButtons.find(button => button.dataset.tab === 'design');
      if (!designTab) return;

      const hasMokoPin = this.dataset.hasMokoPin === 'true';
      const hasEngravingImage = this.currentVariantHasEngravingImage();
      const designCount = this.getDesignCount();

      if (!hasMokoPin && !hasEngravingImage && designCount === 0) {
        designTab.classList.add('hidden');

        if (this.activeTab === 'design') {
          this.switchTab('colors');
        }
      } else {
        designTab.classList.remove('hidden');
      }
    }

    getDesignCount() {
      if (!this.productId) return 0;

      const variantSwitcher = document.querySelector('product-variant-switcher');
      if (!variantSwitcher) return 0;

      const productData = variantSwitcher.productData;
      if (!productData || !productData.variants) return 0;

      let count = 0;

      productData.variants.forEach(variant => {
        const storageKey = `customization_${this.productId}_${variant.id}`;
        const savedData = localStorage.getItem(storageKey);

        if (savedData) {
          try {
            const customizationData = JSON.parse(savedData);
            const hasContent = !!(
              (customizationData.text?.content) ||
              customizationData.pattern ||
              customizationData.color
            );

            if (hasContent) {
              count++;
            }
          } catch (e) {
            console.error('[ProductTabs] Failed to parse saved customization:', e);
          }
        }
      });

      return count;
    }

  }

  if (!customElements.get('product-tabs')) {
    customElements.define('product-tabs', ProductTabs);
  }

})();
